import { useState, type FormEvent, type JSX } from 'react';
import { Link } from 'react-router';
import { Button, Card, Field, Input, StatusIndicator } from '@viniciomendesr/rayrovisk-design-system';
import { ApiError } from '../api/client';
import { setPassword } from '../api/password';
import { AuthLayout, AuthText } from './PasswordScreenParts';

/**
 * Where a password gets set, and the only place one ever does (ADR-0019).
 *
 * It serves two arrivals that are the same act: someone already signed in who
 * decides she wants a second door, and someone who followed a recovery link,
 * which signs her in and lands her here. One screen, because "set your first
 * password" and "choose a new one" differ only in how she got here.
 */
export function PasswordSetupScreen(): JSX.Element {
  const [password, setValue] = useState('');
  const [repeat, setRepeat] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setExpired(false);

    // Caught here rather than at the service: a typo she cannot see is the whole
    // reason the second field exists, and the service has no way to know the two
    // were meant to match.
    if (password !== repeat) {
      setProblem('As duas senhas não conferem.');
      return;
    }

    setBusy(true);
    try {
      await setPassword(password);
      setValue('');
      setRepeat('');
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // She may be holding a link from an email that is hours old. "401" is
        // not an explanation; asking for a fresh one is.
        setExpired(true);
        return;
      }
      setProblem(
        error instanceof Error && error.message
          ? error.message
          : 'Não conseguimos salvar a senha. Tente de novo.'
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <Card layer="functional" elevation="raised" padding="lg">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <StatusIndicator status="live" label="Senha salva" />
            <h1 style={{ margin: 0, font: '400 24px/1.15 var(--rvk-font-expressive)' }}>
              Pronto. Agora você tem duas portas.
            </h1>
            {/* Said out loud because it is invisible otherwise, and someone who
                reset her senha because suspeitava de alguém precisa saber que
                aquele alguém caiu fora (§12.3). */}
            <AuthText>
              Encerramos as sessões em outros dispositivos. Só esta continua aberta. O link por
              e-mail segue funcionando como sempre.
            </AuthText>
            <Link to="/biblioteca">
              <Button variant="primary">Ir para a biblioteca</Button>
            </Link>
          </div>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card layer="functional" elevation="raised" padding="lg">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
              Definir uma senha
            </h1>
            <AuthText>
              É opcional. Serve para entrar sem esperar o e-mail, e o link continua valendo.
            </AuthText>
          </div>

          {expired && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <StatusIndicator status="failed" label="O link expirou" />
              <AuthText>
                Peça outro e abra o mais recente: cada link vale uma vez só.
              </AuthText>
              <Link to="/senha/recuperar">
                <Button variant="secondary" size="sm" type="button">
                  Pedir outro link
                </Button>
              </Link>
            </div>
          )}

          <Field
            label="Nova senha"
            hint="Pelo menos 10 caracteres. Uma frase que você lembre funciona melhor que um código."
            error={problem ?? undefined}
          >
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>

          <Field label="Repita a senha">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </Field>

          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Salvar senha
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
